import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Grid,
  Table,
} from "semantic-ui-react";
import {
  AccordionTitleView,
  CommonTable,
  CustomButton,
  DropDown,
} from "../../../../../components";
import "../styles/CultivarForm.scss";
import { InputNumber } from "../../../../../components/InputText/InputNumber";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import PlusIcon from "../../../../../assets/commonIcon/PlusIcon";
import { useForm } from "react-hook-form";
import { CULTIVATION_TYPE, PesticideTableHeader } from "../../../../../config/constants";
import { useGetPesticideData, useGetPesticideList } from "../../../../../api/static-data";
import { errorView } from "../../../../../helpers/ErrorHandler";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";
import { validateCultivationNumber } from "../../../../../utils/utils";

const Pesticides = forwardRef((
  {
    currentFormData,
    currentDataType,
    currentOriginalFormData
  }: any,
  ref
) => {
  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
  const [enableUpdateButton, setEnableUpdateButton] = useState<any>(false);
  const [pesticide, setPesticide] = useState<any>("");
  const [pesticideDropdownList, setPesticideDropdownList] = useState<any>([]);
  const [pesticideDetails, setPesticideDetails] = useState<any>([]);
  const [pesticideOriginalDetails, setPesticideOriginalDetails] = useState<any>([]);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [deleteValue, setDeleteValue] = useState<any>({
    item: "",
    type: "",
  });

  const originalPesticides =
    currentOriginalFormData?.cultivationField?.pesticideList || [];

  const {
    data: pesticideListData,
    isSuccess: isPesticideListDataSuccess,
    isLoading: isPesticideListDataLoading,
  } = useGetPesticideData();

  const values = useMemo(() => {
    setPesticideDropdownList(pesticideListData)
    return {
      pesticideQuantity: "",
    }
  }, [currentFormData, pesticideListData])

  const {
    formState: { errors },
    control,
    getValues,
    setValue,
    handleSubmit
  } = useForm({ defaultValues: values, values: values });

  useImperativeHandle(ref, () => ({
    getPesticideData: () => {
      return pesticideDetails
    }
  }));

  const AddPesticide = handleSubmit((values) => {
    const selectedPesticideId = parseInt(pesticide);
    const quantity = values.pesticideQuantity;

    if (!pesticide || pesticideDropdownList.length === 0) {
      errorView("Select pesticide name");
      return;
    }

    const updatedTableList = [...pesticideDetails];
    const updatedDropdownList = [...pesticideDropdownList];

    const existingItemIndex = updatedTableList.findIndex(
      (item) => parseInt(item.id) === selectedPesticideId
    );

    if (existingItemIndex !== -1) {
      updatedTableList[existingItemIndex] = {
        ...updatedTableList[existingItemIndex],
        pesticideQuantity: quantity,
      };
    } else {
      const selectedDropdownItem = updatedDropdownList.find(
        (item) => parseInt(item.id) === selectedPesticideId
      );

      if (selectedDropdownItem) {
        updatedTableList.push({
          ...selectedDropdownItem,
          pesticideQuantity: quantity,
        });

        const filteredDropdown = updatedDropdownList.filter(
          (item) => parseInt(item.id) !== selectedPesticideId
        );
        setPesticideDropdownList(filteredDropdown);
      }
    }

    setPesticideDetails(updatedTableList);
    setValue("pesticideQuantity", "");
    setPesticide("");
  });

  const pesticideTableListData = useCallback(() => {
    const pesticideTableList =
      currentFormData?.cultivationField?.pesticideList.map((item: any) => {
        const pesticide = pesticideListData?.find((p: any) => {
          return parseInt(p.id) === parseInt(item.id);
        });
        return {
          ...pesticide,
          ...item,
          id: parseInt(item.id),
        };
      });
    setPesticideDetails(pesticideTableList)
  }, [currentFormData, pesticideListData])


   const pesticideOriginalTableListData = useCallback(() => {
    const pesticideTableList =
      currentOriginalFormData?.cultivationField?.pesticideList.map((item: any) => {
        const pesticide = pesticideListData?.find((p: any) => {
          return parseInt(p.id) === parseInt(item.id);
        });
        return {
          ...pesticide,
          ...item,
          id: parseInt(item.id),
        };
      });
    setPesticideOriginalDetails(pesticideTableList)
  }, [currentFormData, pesticideListData])

  useEffect(() => {
    pesticideTableListData()
    pesticideOriginalTableListData()
  }, [currentFormData, pesticideListData, isPesticideListDataSuccess])

  const onEditPesticides = (item: any) => {
    setValue("pesticideQuantity", item?.pesticideQuantity ?? "");
    setPesticideDropdownList((pesticideList: any) => [
      ...pesticideList,
      item,
    ]);
    setPesticide(item.id.toString());
  }

  const deletepesticides = useCallback(() => {
    setPesticideDetails((prevList: any[]) =>
      prevList.filter((_, index: number) => index !== deleteValue.index)
    );
    setVisibleDeleteModal(false);
    setDeleteValue({});
  }, [deleteValue])

  const handleClick = () => {
    setAccordionEnabled(!accordionEnabled);
  };

  const editPesticides = (item: any) => {
    onEditPesticides(item);
    setEnableUpdateButton(true);
  }

  const deletePesticides = (item: any, index: any) => {
    setVisibleDeleteModal(true);
    setDeleteValue({
      type: "Pesticide",
      item: item ?? "",
      index: index,
    });
  }

  return currentDataType === CULTIVATION_TYPE.Supplier ? (
    <div>
      <AccordionTitleView
        accordionEnabled={false}
        title={"List of Pesticides"}
        plusIcon={false}
      />
      <div>
        <Grid>
          <Grid.Row divided>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              {pesticideOriginalDetails?.length > 0 && (
                <Grid.Column
                  computer={16}
                  tablet={16}
                  mobile={16}
                  className="pesticideTable"
                >
                  <CommonTable
                    tableHeaderData={PesticideTableHeader.filter(
                      (e: any) => e.name !== ""
                    )}
                    className="pesticideTable"
                    isSimulateView
                  >
                    {pesticideOriginalDetails?.map((item: any) => {
                      return (
                        <Table.Row key={`pesticide_${item?.id}`}>
                          <Table.Cell>
                            <p>{item?.pesticide_name ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.pesticideQuantity ?? ""}</p>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </CommonTable>
                </Grid.Column>
              )}
            </Grid.Column>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              <Grid>
                <Grid.Column computer={7} tablet={8} mobile={16}>
                  <DropDown
                    labelName={"Pesticide Name"}
                    placeholder="Select Pesticide Name"
                    defaultValue={pesticide}
                    currentData={pesticideDropdownList}
                    handleChangeState={(e: any, { value }: any) => {
                      setPesticide(value);
                    }}
                    search
                    key={"pesticide_name"}
                  />
                </Grid.Column>
                <Grid.Column computer={7} tablet={6} mobile={16}>
                  <InputNumber
                    transform={{
                      input: (value: any) =>
                        isNaN(value) || value === 0 ? "" : value.toString(),
                      output: (e: any) => {
                        return e;
                      },
                    }}
                    control={control}
                    labelName={"kg/ha"}
                    placeholder="kg/ha"
                    name="pesticideQuantity"
                    errors={errors.pesticideQuantity}
                    required
                    errorMessage={"Quantity is required"}
                    otherValidation={{
                      validate: {
                        valueCheck: (v: any) => validateCultivationNumber(v)
                      }
                    }}
                  />
                </Grid.Column>
                <Grid.Column computer={2} tablet={2} mobile={16}>
                  <CustomButton
                    theme="green"
                    customColumnStyle={"addUnitSize"}
                    icon
                    iconChild={<PlusIcon />}
                    customBase="productUnitAddButton"
                    onClick={() => {
                      AddPesticide();
                      setEnableUpdateButton(false);
                    }}
                  />
                </Grid.Column>
              </Grid>
              {pesticideDetails?.length > 0 && (
                <Grid.Column
                  computer={16}
                  tablet={16}
                  mobile={16}
                  className="pesticideTable"
                >
                  <CommonTable
                    tableHeaderData={PesticideTableHeader}
                    className="pesticideTable"
                  >
                    {pesticideDetails?.map((item: any, index: any) => {
                      return (
                        <Table.Row key={`pesticide_${item?.id}`}>
                          <Table.Cell>
                            <p>{item?.pesticide_name ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.pesticideQuantity ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <Grid>
                              <Grid.Column computer={8} tablet={16} mobile={16}>
                                <MdModeEditOutline
                                  cursor={"pointer"}
                                  size={24}
                                  color="var(--tableEditIcon)"
                                  onClick={() => editPesticides(item)}
                                />
                              </Grid.Column>
                              <Grid.Column computer={8} tablet={16} mobile={16}>
                                <MdDeleteOutline
                                  cursor={"pointer"}
                                  size={24}
                                  color="var(--tableEditIcon)"
                                  onClick={() => deletePesticides(item, index)}
                                />
                              </Grid.Column>
                            </Grid>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </CommonTable>
                </Grid.Column>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
          setDeleteValue({});
        }}
        approve={deletepesticides}
        title={`Delete ${deleteValue.type}`}
        subTitle={`Are you sure you want to delete Pesticides data?`}
      />
    </div>
  ) : null;
})

export default Pesticides;

